import { ArrowRight, ArrowUpRight, ChevronDown, Compass, ShieldCheck } from "lucide-react";
import { useState, type CSSProperties, type ReactNode } from "react";
import type { Destination, Product, Region } from "../types";
import { KuspitNotice, PesoAPesoNotice } from "./Disclosures";
import { TRAVEL_AGENCY_NOTICE } from "@aventuria/client-core/disclosures";
import { priceWithCurrency } from "../lib/format";
import { productIcon } from "../lib/product-icons";
import { SLOT_WIDTHS, mediaVariant, slotBackground, slotSrcSet, type PhotoSlot } from "../lib/media";

const TINTS: Record<string, string> = {
  mediterraneo: "#002557", caribe: "#0031d4", maldivas: "#0179e2", waikiki: "#3dffdf"
};

/** A product description may carry more than one line (admins separate them with a line break). */
export function tagLines(tag: string): string[] {
  return tag.split("\n").map((line) => line.trim()).filter(Boolean);
}

/** Photo (when the catalogue has one) under the design's navy gradient; a brand gradient otherwise. */
/** A photo behind a shade, at the variant size of its slot (see lib/media.ts). */
export function photoBackground(url: string | null | undefined, shade: string, slot: PhotoSlot = "card"): CSSProperties {
  return url
    ? { backgroundImage: `${shade}, ${slotBackground(url, slot)}` }
    : { backgroundImage: `${shade}, ${BRAND_GRADIENT}` };
}

const BRAND_GRADIENT = "linear-gradient(135deg, #0031d4, #0179e2 60%, #3dffdf)";

/**
 * For grids of many photos (trip cards): a lazy <img> with a srcset instead of a CSS background,
 * so only the cards on screen download, at the size the layout needs. The host keeps the shade
 * in `--shade` (drawn by `.photo-host::before` between the photo and the text).
 */
export function photoHost(url: string | null | undefined, shade: string): CSSProperties {
  return { ["--shade" as string]: shade, backgroundImage: url ? undefined : BRAND_GRADIENT };
}

export function LazyPhoto({ url, slot = "card", sizes }: { url: string | null | undefined; slot?: PhotoSlot; sizes: string }) {
  if (!url) return null;
  return <img className="photo-host-img" src={mediaVariant(url, SLOT_WIDTHS[slot][0])} srcSet={slotSrcSet(url, slot)} sizes={sizes} alt="" loading="lazy" decoding="async" />;
}

const CARD_SHADE = "linear-gradient(180deg, rgba(0,37,87,0.15) 0%, rgba(0,37,87,0.85) 100%)";
const REGION_SHADE = "linear-gradient(180deg, rgba(0,37,87,0.05) 0%, rgba(0,37,87,0.35) 50%, rgba(0,37,87,0.95) 100%)";
const DEST_SHADE = "linear-gradient(180deg, transparent 35%, rgba(0,37,87,0.92) 100%)";

/** "Finanzas" product card (Desktop · Productos Hub). */
export function ProductCard({ product, meta, onClick }: { product: Product; meta?: ReactNode; onClick(): void }) {
  const Icon = productIcon(product.icon);
  const tint = TINTS[product.tint] ?? TINTS.maldivas;
  return (
    <button type="button" className="product-card" onClick={onClick} aria-label={`${product.eyebrow}: ${product.title}`}>
      <div className="product-photo" style={photoBackground(product.photoUrl, CARD_SHADE, "half")}>
        <span className="product-eyebrow">{product.eyebrow}</span>
        {/* Only when the catalogue carries a label. Clearing it in admin is how a product stops
            advertising a rate, so an empty label must render nothing rather than an empty pill. */}
        {product.rateLabel.trim() && (
          <span className="glass-pill product-rate"><em>{product.rateLabel}</em> {product.rateSuffix}</span>
        )}
        {product.badge && <span className="product-badge">{product.badge}</span>}
        <strong className="product-title">{product.title}</strong>
      </div>
      <div className="product-foot">
        <span className="product-icon" style={{ background: `linear-gradient(135deg, ${tint}, #3dffdf)` }}><Icon size={19} aria-hidden="true" /></span>
        <span className="product-tag">{tagLines(product.tag).map((line) => <span key={line}>{line}</span>)}{meta && <span className="product-meta">{meta}</span>}</span>
        <ArrowRight size={17} aria-hidden="true" className="product-arrow" />
      </div>
    </button>
  );
}

/**
 * Page-foot disclosure. `travel` (default) says who provides what: Aventuria the trips, Kuspit the
 * wallet, Peso a Peso the credit. `wallet` and `credit` carry the provider's own notice.
 */
export function RegulatedNote({ kind = "travel" }: { kind?: "travel" | "wallet" | "credit" }) {
  if (kind === "credit") return <div className="regulated-note legal"><PesoAPesoNotice /></div>;
  if (kind === "wallet") return <div className="regulated-note legal"><KuspitNotice className="legal-notice" /></div>;
  return (
    <div className="regulated-note">
      <ShieldCheck size={14} aria-hidden="true" />
      <span>{TRAVEL_AGENCY_NOTICE}</span>
    </div>
  );
}

/**
 * What a region offers: its trips plus any destination card not already linked to one of them
 * (a linked destination opens that same trip, so it is not counted twice).
 */
export function regionOfferCount(region: Region, trips: Array<{ region: { id: string } | null; destination: { id: string } | null }>, destinations: Region["destinations"] = region.destinations): number {
  const inRegion = trips.filter((trip) => trip.region?.id === region.id);
  const linked = new Set(inRegion.map((trip) => trip.destination?.id).filter(Boolean));
  return inRegion.length + destinations.filter((destination) => !linked.has(destination.id)).length;
}

/** "POR REGIÓN" card (Desktop · Discover). */
export function RegionCard({ region, count, onClick }: { region: Region; count: number; onClick(): void }) {
  return (
    <button type="button" className="region-card" onClick={onClick} style={photoBackground(region.photoUrl, REGION_SHADE, "half")} aria-label={`Explorar ${region.name}`}>
      <div className="region-top">
        <span className="glass-pill">{count} {count === 1 ? "viaje" : "viajes"}</span>
        <span className="glass-circle"><ArrowUpRight size={16} aria-hidden="true" /></span>
      </div>
      <div className="region-body">
        {region.featured && (
          <span className="region-featured">{region.flag && <span aria-hidden="true">{region.flag}</span>}<span>Destacado · {region.featured}</span></span>
        )}
        <strong>{region.name}</strong>
        {region.subtitle && <span className="region-subtitle">{region.subtitle}</span>}
        <span className="region-cta">
          <span><Compass size={15} aria-hidden="true" />{region.hint ?? "Explorar región"}</span>
          <span>EXPLORAR →</span>
        </span>
      </div>
    </button>
  );
}

/** Destination photo card (destinations.jsx DestCard) with the price footer. */
export function DestinationCard({ destination, onClick, action = "Ahorrar para este viaje" }: { destination: Destination; onClick(): void; action?: string }) {
  const [expanded, setExpanded] = useState(false);
  const expandable = Boolean(destination.description);
  const detailsId = `dest-details-${destination.id}`;
  return (
    <article className="dest-card">
      <div className="dest-photo" style={photoBackground(destination.photoUrl, DEST_SHADE)}>
        <span className="dest-category">{destination.category}</span>
        <div className="dest-copy">
          <span className="dest-country">{destination.country}</span>
          <strong>{destination.name}</strong>
          {destination.subtitle && <span className="dest-sub">{destination.subtitle}</span>}
        </div>
      </div>
      <div className="dest-foot">
        <div>
          {destination.price !== null && (
            <strong><span translate="no">{priceWithCurrency(destination.price, destination.currency)}</span> <small className="dest-per-person">por persona</small></strong>
          )}
          <span>{[destination.season, destination.nights ? `${destination.nights} noches` : null].filter(Boolean).join(" · ") || "Consulta fechas"}</span>
        </div>
        <button type="button" className="secondary-button small" onClick={onClick}>{action} <ArrowRight size={14} aria-hidden="true" /></button>
      </div>
      {destination.perks.length > 0 && (
        <ul className="dest-perks">{destination.perks.map((perk) => <li key={perk}>{perk}</li>)}</ul>
      )}
      {expandable && (
        <>
          <button type="button" className={`dest-more${expanded ? " open" : ""}`} onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} aria-controls={detailsId}>
            {expanded ? "Menos detalles" : "Más detalles"} <ChevronDown size={14} aria-hidden="true" />
          </button>
          {expanded && <p className="dest-description" id={detailsId}>{destination.description}</p>}
        </>
      )}
    </article>
  );
}
